/**
 * Der globale Fehlerfaenger (Fehleranzeige Paket 1, Spec
 * `docs/superpowers/specs/2026-10-19-fehleranzeige-fundament-design.md`, Abschnitt 3): hoert auf
 * `unhandledrejection` und `error` am Fenster und leitet beides an `meldeFehler` (ohne Kontext).
 * Er ist ein Netz fuer Vergessenes, kein Ersatz fuer die Umstellung der Aufrufstellen - wer einen
 * Fehler kennt, meldet ihn selbst mit einem Kontext, der sagt, was scheiterte.
 *
 * Installiert wird er einmal an der Wurzel (`src/App.tsx`, `useEffect`). Der Abbau entfernt BEIDE
 * Hoerer wirklich: StrictMode installiert, baut ab und installiert erneut - bliebe ein Hoerer vom
 * ersten Mal stehen, meldete jedes Ereignis doppelt.
 *
 * Nicht gemeldet wird, was `meldeFehler` ohnehin ausfiltert (Abbruch, `SitzungsFehler`: der Text ist
 * `null`) und zwei Eintraege, die der Browser ohne Fehler ausloest:
 * - "ResizeObserver loop ..." - harmlos, der Browser holt den Rahmen im naechsten Takt nach. Die
 *   Wortlaute unterscheiden sich ("... loop limit exceeded", "... loop completed with undelivered
 *   notifications."), deshalb gilt das Praefix.
 * - "Script error." ohne Fehlerobjekt - der Browser verschweigt bei fremder Herkunft jede Einzelheit;
 *   es gibt nichts, was man dem Nutzer oder dem Entwickler zeigen koennte.
 *
 * `preventDefault()` wird nie gerufen: die Konsole behaelt ihre Ausgabe, der Entwickler sieht den
 * Fehler dort weiterhin wie bisher.
 *
 * Zeichenketten werden nie roh gemeldet. `fehlertext` gibt einen String als fertigen Nutzersatz
 * durch; ein `message` wie "Uncaught ReferenceError: foo is not defined" oder ein
 * `Promise.reject("TypeError: ...")` zeigte sonst Technik. Sie werden deshalb in einen `Error`
 * verpackt und ergeben den Sammeltext (`FEHLERTEXTE.unerwartet`) samt `console.error`.
 */
import { meldeFehler } from "../state/fehlerStapel.ts";

const RESIZE_OBSERVER_PRAEFIX = "ResizeObserver loop";
const SCRIPT_ERROR = /^script error\.?$/i;

/** Ein String ist nie ein Nutzersatz, wenn er aus dem Browser kommt - siehe Kopfkommentar. */
function alsFehler(wert: unknown): unknown {
  return typeof wert === "string" ? new Error(wert) : wert;
}

function meldeFehlerEreignis(ereignis: Event & { error?: unknown; message?: unknown }): void {
  const { error, message } = ereignis;
  const meldung = typeof message === "string" ? message : "";

  if (meldung.startsWith(RESIZE_OBSERVER_PRAEFIX)) return;
  if (error !== undefined && error !== null) {
    meldeFehler(alsFehler(error));
    return;
  }
  if (SCRIPT_ERROR.test(meldung.trim())) return;
  meldeFehler(new Error(meldung));
}

/**
 * Haengt die beiden Hoerer an `ziel` (Vorgabe: `window`) und gibt den Abbau zurueck. Der Abbau darf
 * mehrfach gerufen werden.
 *
 * Jeder Aufruf legt eigene Hoerer an. Das ist Absicht: gaeben alle Installationen dieselbe
 * Funktionsreferenz, wiese `addEventListener` die zweite als Doppelte ab, und ein Abbau, der nichts
 * entfernt, fiele nicht auf - der Neuaufbau unter StrictMode bliebe scheinbar korrekt.
 */
export function installiereGlobalenFehlerfaenger(ziel: Window = window): () => void {
  const beiAblehnung = (ereignis: Event): void => {
    meldeFehler(alsFehler((ereignis as Event & { reason?: unknown }).reason));
  };
  const beiFehler = (ereignis: Event): void => {
    meldeFehlerEreignis(ereignis as Event & { error?: unknown; message?: unknown });
  };
  ziel.addEventListener("unhandledrejection", beiAblehnung);
  ziel.addEventListener("error", beiFehler);
  return () => {
    ziel.removeEventListener("unhandledrejection", beiAblehnung);
    ziel.removeEventListener("error", beiFehler);
  };
}
